import {Component, OnDestroy, OnInit} from '@angular/core';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {firstValueFrom} from 'rxjs';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {Store} from '@ngxs/store';
import {ILoaderModel, Loader, LoaderState} from '../../models/loader/loader.redux';
import {GenderEnum} from '../../models/user';
import {takeWhile} from 'rxjs/operators';
import {AppTourService} from '../../services/app-tour/app-tour.service';
import {ConsultationInformationComponent} from '../consultation-information/consultation-information.component';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {StorageService} from '../../services/storage/storage.service';
import {IUser} from '../../models/auth';
import {LocalStorageEnum} from '../../enums/utils';
import {ToursEnum} from '../../enums/tour';
import {NzModalService} from 'ng-zorro-antd/modal';
import {ConsultationFormulasComponent} from '../consultation-formulas/consultation-formulas.component';
import {AppointmentIndicatorActions} from '../../models/appointment-indicator';
import {TourAction, TourState} from '../../models/tour';
import {SkeletonService} from '../../services/skeleton/skeleton.service';
import {SkeletonEnum} from '../../enums/skeleton';
import {ConsultationMeasurementComponent} from '../consultation-measurement/consultation-measurement.component';
import {MeasurementAction} from '../../models/measurement';

export interface IRouteTabs {
  img?: string;
  hide?: boolean;
  disabled?: boolean;
  queryParams?: Params;
  route?: string;
  path: string;
  title: string;
  metadata?: any;
  exact?: boolean;
}

@Component({
  selector: 'app-appointment-page',
  templateUrl: './appointment-page.component.html',
  styleUrls: ['./appointment-page.component.scss'],
  animations: [fadeInAnimation],
})
export class AppointmentPageComponent implements OnInit, OnDestroy {
  consultationInformationComponent: ConsultationInformationComponent;
  consultationFormulasComponent: ConsultationFormulasComponent;
  consultationMeasurementComponent: ConsultationMeasurementComponent;
  // Variable usada para guardar los cambios del paso de fórmulas al menos una vez
  // En los pasos se comprueba que ambas variables (basicFormulasComponent.basicFormulasSaved and basicFormulasSaved) sean true
  // Para no solicitar que se guarden cambios en caso de que se regrese a un paso anterior.
  basicFormulasSaved = false;

  routes: IRouteTabs[] = [
    { title: 'Información del paciente', path: 'information' },
    { title: 'Fichas del paciente', path: 'file' },
    { title: 'Mediciones del paciente', path: 'measurements' },
    { title: 'Fórmulas', path: 'formulas' },
    { title: 'Diagnóstico del paciente', path: 'diagnostic' },
    { title: 'Orientación alimenticia', path: 'orientation' },
    { title: 'Descargar informe', path: 'download-patient-template' },
  ];
  currentIndex: number;
  params: Params;
  appointmentId: string;

  genderEnum = GenderEnum;
  loadTabs: boolean;
  skeletonTemplate: any[] = [];

  isActiveObservables: boolean;

  constructor(
    private router: Router,
    public utilitiesService: UtilitiesService,
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private appTourService: AppTourService,
    private storageService: StorageService,
    private messageAlertService: MessageAlertService,
    private nzModalService: NzModalService,
    private skeletonService: SkeletonService,
  ) { }

  ngOnInit(): void {
    this.initValues();
    this.listeners();
    this.checkAdviceTour();
  }

  private checkAdviceTour(): void {
    const {user} = this.storageService.getLocalItem<{user: IUser, token: string}>(LocalStorageEnum.USER);
    if (!user) return;

    const {advice} = user.metadata.tours;
    const forceTourName = this.storageService.getLocalItem(LocalStorageEnum.FORCE_TOUR_NAME);

    if (advice === false || forceTourName === ToursEnum.ADVICE) {
      this.appTourService.showTour('advice').finally(async () => {
        if (this.appTourService.tourMode) {
          // When is tourMode redirect to information page
          const {clinicId, patientId} = this.params;
          const {appointmentId} = this.activatedRoute.snapshot.queryParams;
          await firstValueFrom(this.store.dispatch(new TourAction.CreateDemoAppointment(clinicId)));
          const demoAppointment = await firstValueFrom(this.store.selectOnce(TourState.getDemoAppointmentCreated));
          if (!demoAppointment) return;

          await this.router.navigate(
            ['/', 'clinic', clinicId, 'patients', patientId, 'consultation', 'information'],
            {
              queryParams: {
                ...this.activatedRoute.snapshot.queryParams,
                appointmentId: demoAppointment._id,
              },
            }
          );
          await firstValueFrom(this.store.dispatch(new AppointmentIndicatorActions.SetIndicatorVisibility(true, demoAppointment._id)));

          this.currentIndex = 0;
          if (demoAppointment._id !== appointmentId) {
            await this.messageAlertService.createMessage('info', 'Se ha cambiado la cita para realizar el tour.');
            window.location.reload();
          }
        }
      });
    }
  }

  initValues(): void {
    this.params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.isActiveObservables = true;
    this.currentIndex = 0;
    this.loadTabs = false;
    const { clinicId, patientId } = this.params;
    this.appointmentId = this.activatedRoute.snapshot.queryParams.appointmentId;
    const defaultPath = `/clinic/${clinicId}/patients/${patientId}/consultation/`;
    this.routes = this.routes.map((route: IRouteTabs) => ({
      ...route,
      route: defaultPath + route.path,
    }));
    const step = this.router.url.split('?')[0].split('/').pop();
    this.currentIndex = this.routes.findIndex(route => route.path === step);
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.CONTAINER);
  }

  private listeners(): void {
    // Solo muestra el esqueleto la primera vez
    this.store.select(LoaderState.getLoaderStatus)
      .pipe(
        takeWhile(() => this.isActiveObservables && !this.loadTabs),
      ).subscribe((_: ILoaderModel): void => {
        this.loadTabs = true;
      });
  }

  async pre(): Promise<void> {
    this.currentIndex -= 1;
    await this.router.navigate(['clinic', this.params.clinicId, 'patients', this.params.patientId, 'consultation', this.routes[this.currentIndex].path],
      { queryParams: { ...this.activatedRoute.snapshot.queryParams, appointmentId: this.appointmentId } });
  }

  async next(): Promise<void> {
    await firstValueFrom(this.store.dispatch(new Loader(true)));
    if (this.currentIndex === 0) { // Step information
      if (this.consultationInformationComponent?.form?.invalid) {
        await this.messageAlertService.createMessage('info', 'Antes de avanzar completa los datos básica del usuario.');
        this.consultationInformationComponent?.onSetEditionMode(true);
        this.consultationInformationComponent?.form?.markAllAsTouched();
        return await firstValueFrom(this.store.dispatch(new Loader(false)));
      } else if (this.consultationInformationComponent?.patientInfo?.isEdit) {
        await this.messageAlertService.createMessage('info', 'Guarda cambios antes de continuar.');
        return await firstValueFrom(this.store.dispatch(new Loader(false)));
      }
    } else if (this.currentIndex === 2 && this.consultationMeasurementComponent.hasUnsavedMeasurements) {
      // Tab de mediciones (Muestra mensaje cuando tenga mediciones sin guardar)
      await firstValueFrom(this.store.dispatch(new Loader(false)));
      return this.stepNzModalUnsavedMeasurements();
    } else if (this.currentIndex === 3) { // Step formulas

      this.basicFormulasSaved = this.consultationFormulasComponent.basicFormulasComponent?.basicFormulasSaved || this.basicFormulasSaved;
      if (!this.basicFormulasSaved) {
        await firstValueFrom(this.store.dispatch(new Loader(false)));
        return this.stepFormulaModalValidations();
      }
    }

    this.currentIndex += 1;
    await this.router.navigate(['clinic', this.params.clinicId, 'patients', this.params.patientId, 'consultation', this.routes[this.currentIndex].path],
      { queryParams: { ...this.activatedRoute.snapshot.queryParams, appointmentId: this.appointmentId } });
    await firstValueFrom(this.store.dispatch(new Loader(false)));
  }


  // Terminar de revisar esta función porque no se está mostrando el modal.
  private stepFormulaModalValidations(): void {
    this.nzModalService.confirm({
      nzTitle: '¿Deseas continuar sin guardar cambios?',
      nzContent: 'Guarda los valores calculados de las fórmulas para obtener un mejor diagnostico',
      nzOkText: 'SÍ, GUARDAR',
      nzCancelText: 'NO',
      nzOkDanger: true,
      nzClassName: 'modal-confirm button-primary',
      nzClosable: false,
      nzOnOk: async () => {
        this.basicFormulasSaved = true;
        await this.consultationFormulasComponent.basicFormulasComponent?.saveBasicFormulas();
        await this.next();
      },
      nzOnCancel: async () => {
        this.basicFormulasSaved = true;
        await this.next();
      }
    });
  }

  private stepNzModalUnsavedMeasurements(): void {
    this.nzModalService.confirm({
      nzTitle: '¿Deseas continuar sin guardar cambios?',
      nzContent: 'Guarda las mediciones agregadas para obtener un mejor diagnostico',
      nzOkText: 'SÍ, GUARDAR',
      nzCancelText: 'NO',
      nzOkDanger: true,
      nzClassName: 'modal-confirm button-primary',
      nzClosable: false,
      nzOnOk: async (): Promise<void> => {
        await this.consultationMeasurementComponent.saveBasicFormulas();
        await this.next();
      },
      nzOnCancel: async (): Promise<void> => {
        // await this.next();
      }
    });
  }

  ngOnDestroy(): void {
    (async (): Promise<void> => {
      await firstValueFrom((this.store.dispatch(new MeasurementAction.Reset())));
    })();
    this.isActiveObservables = false;
  }

  /**
   *
   * @param component ConsultationInformationComponent
   */
  routerOutlet(component: any): void {
    if (this.currentIndex === 0) { // Step information
      this.consultationInformationComponent = component;
    } else if (this.currentIndex === 2) {
      this.consultationMeasurementComponent = component;
    } else if (this.currentIndex === 3) {
      this.consultationFormulasComponent = component;
    }
  }
}
